import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Switch,
} from '@lognai/synapse-design-system-v2';
import { Lock } from '@lognai/synapse-design-system-v2/icons';
import {
  usePreferences,
  useNotificationMails,
  useUpdatePreferences,
} from '../hooks/useAccountSettings';
import type { NotificationMail } from '../services/api/auth';

/**
 * Personal opt-outs for individual notification emails. The API replaces the
 * stored `mutedTypes` array wholesale, so every toggle sends the full new list.
 */
export const EmailMutesCard: React.FC = () => {
  const mailsQuery = useNotificationMails();
  const prefsQuery = usePreferences();
  const updatePrefs = useUpdatePreferences();

  const mails = mailsQuery.data ?? [];
  if (mails.length === 0) return null;

  const masterOff = prefsQuery.data?.notifications?.email === false;
  const muted = new Set(
    prefsQuery.data?.notifications?.mutedTypes ?? mails.filter((m) => m.muted).map((m) => m.type)
  );

  const toggle = (type: string, receive: boolean) => {
    const next = receive ? [...muted].filter((t) => t !== type) : [...muted, type];
    updatePrefs.mutate({ notifications: { mutedTypes: next } });
  };

  const byArea = new Map<string, NotificationMail[]>();
  for (const m of mails) byArea.set(m.area, [...(byArea.get(m.area) ?? []), m]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email preferences</CardTitle>
        <CardDescription>
          Choose which emails you receive. Some are needed for you to act and can&apos;t be turned off.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {masterOff && (
          <p className="text-sm text-muted-foreground">Email is turned off above.</p>
        )}
        {updatePrefs.isError && (
          <p role="alert" className="text-xs text-destructive">
            Failed to save preference. Please try again.
          </p>
        )}
        {[...byArea.entries()].map(([area, items]) => (
          <div key={area} className="space-y-3">
            <p className="text-sm font-medium text-foreground">{area}</p>
            {items.map((m) => (
              <div key={m.type} className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm text-foreground">{m.label}</span>
                  <span className="text-xs text-muted-foreground">{m.description}</span>
                  {!m.mutable && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Lock className="h-3 w-3" />
                      Needed for you to act on it
                    </span>
                  )}
                </div>
                <Switch
                  checked={m.mutable ? !muted.has(m.type) : true}
                  disabled={!m.mutable || masterOff || prefsQuery.isLoading || updatePrefs.isPending}
                  onCheckedChange={(checked) => toggle(m.type, checked)}
                  aria-label={m.label}
                />
              </div>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
